(() => {
  'use strict';

  if (customElements.get('wwfs-complementary-products')) return;

  class WWFSComplementaryProducts extends HTMLElement {
    connectedCallback() {
      if (this.dataset.wwfsConnected === 'true') return;

      this.dataset.wwfsConnected = 'true';
      this.priceQueue = Promise.resolve();
      this.loadController = null;

      if (this.querySelector('[data-ready]')) {
        this.initProducts();
        return;
      }

      if (
        (window.Shopify && window.Shopify.designMode) ||
        !('IntersectionObserver' in window)
      ) {
        this.loadRecommendations();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0] || !entries[0].isIntersecting) return;

          this.observer.disconnect();
          this.observer = null;
          this.loadRecommendations();
        },
        {
          rootMargin: '400px 0px',
          threshold: 0
        }
      );

      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      if (this.loadController) {
        this.loadController.abort();
        this.loadController = null;
      }
    }

    async loadRecommendations() {
      if (!this.dataset.url || this.dataset.loading === 'true') return;

      this.dataset.loading = 'true';

      if (this.loadController) {
        this.loadController.abort();
      }

      this.loadController = new AbortController();

      try {
        const separator = this.dataset.url.includes('?') ? '&' : '?';
        const response = await fetch(
          this.dataset.url + separator + '_wwfs=' + Date.now(),
          {
            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            },
            signal: this.loadController.signal
          }
        );

        if (!response.ok) {
          throw new Error(
            `Unable to load complementary products: ${response.status}`
          );
        }

        const html = await response.text();
        const template = document.createElement('template');
        template.innerHTML = html;

        const candidates = Array.from(
          template.content.querySelectorAll(
            'wwfs-complementary-products'
          )
        );

        const source =
          candidates.find(
            (candidate) =>
              String(candidate.dataset.productId || '') ===
              String(this.dataset.productId || '')
          ) ||
          candidates[0];

        if (!source) {
          this.innerHTML = '';
          return;
        }

        this.innerHTML = source.innerHTML;

        if (this.querySelector('[data-ready]')) {
          this.initProducts();
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn(
            'WWFS: complementary products could not be loaded.',
            error
          );
        }
      } finally {
        this.dataset.loading = 'false';
      }
    }

    initProducts() {
      this.cards = Array.from(this.querySelectorAll('[data-card]'));
      this.addButton = this.querySelector('[data-add-selected]');
      this.buttonText = this.querySelector('[data-button-text]');
      this.count = this.querySelector('[data-count]');
      this.status = this.querySelector('[data-status]');

      if (!this.cards.length || !this.addButton) return;

      this.cards.forEach((card) => {
        const checkbox = card.querySelector('[data-select]');
        const select = card.querySelector('[data-variant-select]');

        if (checkbox && checkbox.dataset.bound !== 'true') {
          checkbox.dataset.bound = 'true';

          checkbox.addEventListener('change', () => {
            card.classList.toggle(
              'is-selected',
              checkbox.checked
            );

            this.updateCount();
          });
        }

        if (select && select.dataset.bound !== 'true') {
          select.dataset.bound = 'true';

          select.addEventListener('change', () => {
            this.syncVariant(card);

            /*
             * A deliberate option selection is a strong signal that the
             * customer wants this accessory, so select it automatically.
             */
            if (checkbox && !checkbox.checked) {
              checkbox.checked = true;
              card.classList.add('is-selected');
            }

            this.updateCount();
          });
        }

        this.renderNativePrice(card);
        this.queueWholesalePrice(card);
      });

      if (this.addButton.dataset.bound !== 'true') {
        this.addButton.dataset.bound = 'true';

        this.addButton.addEventListener(
          'click',
          this.addSelected.bind(this)
        );
      }

      this.updateCount();
    }

    syncVariant(card) {
      const select = card.querySelector('[data-variant-select]');
      const option = select ? select.selectedOptions[0] : null;

      if (!option) return;

      card.dataset.variantId = option.value;
      card.dataset.variantSku = option.dataset.sku || '';
      card.dataset.variantPrice = option.dataset.price || '0';
      card.dataset.variantCompare = option.dataset.compare || '0';
      card.dataset.priceText = option.dataset.priceText || '';
      card.dataset.compareText = option.dataset.compareText || '';
      card.dataset.variantQuantity = option.dataset.quantity || '1';

      this.renderNativePrice(card);
      this.queueWholesalePrice(card);
    }

    renderNativePrice(card) {
      const current = card.querySelector('[data-current-price]');
      const compare = card.querySelector('[data-compare-price]');

      if (current) {
        current.textContent = card.dataset.priceText || '';
      }

      if (compare) {
        const price = Number(card.dataset.variantPrice || 0);
        const compareAt = Number(card.dataset.variantCompare || 0);

        if (
          compareAt > price &&
          card.dataset.compareText
        ) {
          compare.textContent = card.dataset.compareText;
          compare.hidden = false;
        } else {
          compare.textContent = '';
          compare.hidden = true;
        }
      }
    }

    queueWholesalePrice(card) {
      if (this.dataset.useWholesale !== 'true') return;

      this.priceQueue = this.priceQueue
        .catch(() => {})
        .then(() => this.applyWholesalePrice(card));
    }

    waitForWSAIO(maxWait = 2500) {
      const started = Date.now();

      return new Promise((resolve) => {
        const check = () => {
          if (
            window.WSAIO &&
            typeof window.WSAIO.productDiscount === 'function'
          ) {
            resolve(window.WSAIO);
            return;
          }

          if (Date.now() - started >= maxWait) {
            resolve(null);
            return;
          }

          window.setTimeout(check, 75);
        };

        check();
      });
    }

    resolvePriceState(response, card, wsaio) {
      const basePrice =
        Number(card.dataset.variantPrice || 0) / 100;

      if (!Number.isFinite(basePrice) || basePrice <= 0) {
        return null;
      }

      let breaks = [];
      const volume = response && response.volume_discount;

      if (volume && Array.isArray(volume.quantity_breaks)) {
        breaks = volume.quantity_breaks;
      }

      if (
        volume &&
        volume.product_volume_discount &&
        Array.isArray(
          volume.product_volume_discount.volume_discount
        )
      ) {
        breaks =
          volume.product_volume_discount.volume_discount;
      }

      /*
       * Complementary items are added at their normal minimum quantity.
       * Use that quantity here so a tier starting at 1 is still represented,
       * while larger multibuy tiers remain a cart / product-page concern.
       */
      const quantity = Math.max(
        1,
        Number(card.dataset.variantQuantity || 1)
      );

      let activeBreak = null;

      breaks.forEach((tier) => {
        const tierQty = Number(tier && tier.qty);

        if (
          Number.isFinite(tierQty) &&
          tierQty > 0 &&
          quantity >= tierQty &&
          (!activeBreak ||
            tierQty > Number(activeBreak.qty))
        ) {
          activeBreak = tier;
        }
      });

      if (
        activeBreak &&
        wsaio &&
        typeof wsaio.get_discount === 'function'
      ) {
        try {
          const tierDiscount = Number(
            wsaio.get_discount(
              basePrice,
              activeBreak.type,
              activeBreak.value
            )
          );

          const tierPrice = basePrice - tierDiscount;

          if (
            Number.isFinite(tierDiscount) &&
            tierDiscount > 0 &&
            Number.isFinite(tierPrice) &&
            tierPrice >= 0 &&
            tierPrice < basePrice
          ) {
            return {
              current: tierPrice,
              original: basePrice
            };
          }
        } catch (error) {}
      }

      const regular =
        response && response.regular_discount;

      const regularSaved = Number(
        regular && regular.ws_discount
      );

      const regularPrice = basePrice - regularSaved;

      if (
        Number.isFinite(regularSaved) &&
        regularSaved > 0 &&
        Number.isFinite(regularPrice) &&
        regularPrice >= 0 &&
        regularPrice < basePrice
      ) {
        return {
          current: regularPrice,
          original: basePrice
        };
      }

      return null;
    }

    async applyWholesalePrice(card) {
      const wsaio = await this.waitForWSAIO();

      if (!wsaio || !card.isConnected) return;

      const requestVariantId = Number(
        card.dataset.variantId || 0
      );

      if (!requestVariantId) return;

      const result = await new Promise((resolve) => {
        let settled = false;

        const timeout = window.setTimeout(() => {
          if (settled) return;
          settled = true;
          resolve(null);
        }, 1400);

        const hasOwnGlobalCallback =
          Object.prototype.hasOwnProperty.call(
            wsaio,
            'callback'
          );

        const globalCallback = wsaio.callback;

        try {
          /*
           * Same safe output:false approach used by the WWFS collection
           * cards. Suppressing only WSAIO.callback prevents a display-only
           * price lookup from overwriting cart pre-checkout data.
           */
          wsaio.callback = null;

          wsaio.productDiscount(
            {
              template: 'collection',
              item_price_selector:
                document.createElement('span'),
              product_id: Number(card.dataset.productId || 0),
              product_handle: card.dataset.productHandle || '',
              variant_id: requestVariantId,
              variant_sku: card.dataset.variantSku || '',
              variant_price:
                Number(card.dataset.variantPrice || 0),
              variant_compare_at_price:
                Number(card.dataset.variantCompare || 0),
              collection_id: 'default',
              output: false,
              apply_volume_discount: true
            },
            (response) => {
              if (settled) return;

              settled = true;
              window.clearTimeout(timeout);

              resolve(
                this.resolvePriceState(
                  response,
                  card,
                  wsaio
                )
              );
            }
          );
        } catch (error) {
          if (!settled) {
            settled = true;
            window.clearTimeout(timeout);
            resolve(null);
          }
        } finally {
          if (hasOwnGlobalCallback) {
            wsaio.callback = globalCallback;
          } else {
            delete wsaio.callback;
          }
        }
      });

      /*
       * Do not paint a result for a variant that the customer has already
       * changed while this lookup was queued.
       */
      if (
        !result ||
        Number(card.dataset.variantId || 0) !==
          requestVariantId
      ) {
        return;
      }

      const current = card.querySelector('[data-current-price]');
      const compare = card.querySelector('[data-compare-price]');

      const formatMoney =
        typeof wsaio.formatMoney === 'function'
          ? (value) => wsaio.formatMoney(value)
          : (value) => '£' + Number(value).toFixed(2);

      if (current) {
        current.textContent = formatMoney(result.current);
      }

      if (compare) {
        compare.textContent = formatMoney(result.original);
        compare.hidden = false;
      }
    }

    updateCount() {
      const selected = this.cards.filter((card) => {
        const checkbox = card.querySelector('[data-select]');
        return checkbox && checkbox.checked;
      });

      this.addButton.disabled = selected.length === 0;

      if (this.count) {
        const noun = this.cards.length === 1 ? 'item' : 'items';

        this.count.textContent =
          `${selected.length} of ${this.cards.length} ${noun} selected`;
      }

      if (this.status) {
        this.status.textContent = '';
      }
    }

    selectedItems() {
      return this.cards
        .filter((card) => {
          const checkbox = card.querySelector('[data-select]');
          return checkbox && checkbox.checked;
        })
        .map((card) => ({
          id: Number(card.dataset.variantId || 0),
          quantity: Math.max(
            1,
            Number(card.dataset.variantQuantity || 1)
          )
        }))
        .filter((item) => item.id > 0);
    }

    cartSections(cartDrawer) {
      const sections = ['cart-icon-bubble'];

      if (cartDrawer) {
        const drawerSection =
          cartDrawer.closest('.shopify-section');

        if (drawerSection && drawerSection.id) {
          sections.push(
            drawerSection.id.replace(
              'shopify-section-',
              ''
            )
          );
        }
      }

      return Array.from(new Set(sections));
    }

    dispatchCartEvent(name, detail) {
      this.dispatchEvent(
        new CustomEvent(name, {
          bubbles: true,
          detail
        })
      );
    }

    async addSelected() {
      const items = this.selectedItems();

      if (!items.length || !this.addButton) return;

      const originalText =
        this.buttonText
          ? this.buttonText.textContent
          : 'Add selected';

      this.addButton.disabled = true;
      this.addButton.classList.add('is-loading');

      if (this.buttonText) {
        this.buttonText.textContent =
          items.length === 1
            ? 'Adding item…'
            : 'Adding items…';
      }

      if (this.status) {
        this.status.textContent = '';
      }

      const cartDrawer =
        document.querySelector('cart-drawer');

      const sections = this.cartSections(cartDrawer);

      const root =
        window.Shopify &&
        Shopify.routes &&
        Shopify.routes.root
          ? Shopify.routes.root
          : '/';

      try {
        const response = await fetch(
          root + 'cart/add.js',
          {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              items,
              sections: sections.join(','),
              sections_url: window.location.pathname
            })
          }
        );

        const data = await response.json();

        let error =
          typeof data.description === 'string'
            ? data.description
            : data.message;

        if (data.errors && typeof data.errors === 'object') {
          error = Object.values(data.errors)
            .flat()
            .join(', ');
        }

        if (!response.ok || data.status) {
          throw new Error(
            error ||
            'Sorry, those items could not be added to your cart.'
          );
        }

        const ProductForm =
          customElements.get('product-form');

        if (
          ProductForm &&
          typeof ProductForm.updateCartIcon === 'function'
        ) {
          ProductForm.updateCartIcon(data);
        }

        /*
         * Match the main WWFS product-form behaviour:
         * render the normal Enterprise drawer response after its short delay.
         */
        if (
          window.theme &&
          theme.settings &&
          theme.settings.afterAtc === 'page'
        ) {
          window.setTimeout(() => {
            window.location.href =
              theme.routes && theme.routes.cart
                ? theme.routes.cart
                : root + 'cart';
          }, 300);
        } else {
          window.setTimeout(() => {
            if (
              cartDrawer &&
              typeof cartDrawer.renderContents === 'function'
            ) {
              cartDrawer.renderContents(
                data,
                Boolean(
                  window.theme &&
                  theme.settings &&
                  theme.settings.afterAtc === 'drawer'
                )
              );
            } else if (
              window.theme &&
              theme.routes &&
              window.location.pathname === theme.routes.cart
            ) {
              const cartItems =
                document.querySelector('cart-items');

              if (
                cartItems &&
                typeof cartItems.refresh === 'function'
              ) {
                cartItems.refresh();
              }
            }
          }, 700);
        }

        /*
         * Fetch the completed cart and emit the same confirmed event family
         * used by product-form.js. WWFS cart pricing listens for this event
         * outside the drawer and performs its normal WAIO refresh.
         */
        let completedCart = data;

        try {
          const cartResponse = await fetch(root + 'cart.js');

          if (cartResponse.ok) {
            completedCart = await cartResponse.json();
          }
        } catch (refreshError) {
          console.warn(
            'WWFS: accessories were added, but the follow-up cart refresh failed.',
            refreshError
          );
        }

        this.dispatchCartEvent('on:cart:add', {
          cart: completedCart,
          variantId: items[0].id,
          variantIds: items.map((item) => item.id)
        });

        this.cards.forEach((card) => {
          const checkbox = card.querySelector('[data-select]');

          if (checkbox) {
            checkbox.checked = false;
          }

          card.classList.remove('is-selected');
        });

        if (this.status) {
          this.status.textContent =
            items.length === 1
              ? 'Added to cart ✓'
              : `${items.length} items added to cart ✓`;
        }

        if (this.buttonText) {
          this.buttonText.textContent = 'Added ✓';
        }

        this.updateCount();

        /*
         * updateCount intentionally clears status for interaction changes;
         * put the success message back after that state refresh.
         */
        if (this.status) {
          this.status.textContent =
            items.length === 1
              ? 'Added to cart ✓'
              : `${items.length} items added to cart ✓`;
        }

        window.setTimeout(() => {
          if (this.buttonText) {
            this.buttonText.textContent = originalText;
          }

          if (this.status) {
            this.status.textContent = '';
          }

          this.updateCount();
        }, 1600);
      } catch (error) {
        console.warn(
          'WWFS: complementary add failed.',
          error
        );

        const message =
          error && error.message
            ? error.message
            : 'Sorry, those items could not be added.';

        if (this.status) {
          this.status.textContent = message;
        }

        this.dispatchCartEvent('on:cart:error', {
          error: message
        });

        if (this.buttonText) {
          this.buttonText.textContent = originalText;
        }

        this.addButton.disabled = false;
      } finally {
        this.addButton.classList.remove('is-loading');

        if (!this.selectedItems().length) {
          this.addButton.disabled = true;
        }
      }
    }
  }

  customElements.define(
    'wwfs-complementary-products',
    WWFSComplementaryProducts
  );
})();
